/**
 * Active Residents Filters Component
 *
 * Displays removable badges for each applied filter on the Residents tab,
 * matching the ActiveFilters pattern used by the MDS/Care tabs.
 */

import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import type { RiskLevel } from "../../types/residents";

interface ActiveResidentsFiltersProps {
  selectedRiskLevels: RiskLevel[];
  onRiskLevelsChange: (levels: RiskLevel[]) => void;
  daysSinceReview: string;
  onDaysSinceReviewChange: (value: string) => void;
  selectedBuilding: string;
  onBuildingChange: (value: string) => void;
  buildingOptions: { value: string; label: string }[];
  selectedStation: string[];
  onStationChange: (value: string[]) => void;
  stationOptions: { value: string; label: string }[];
  showHidden: boolean;
  onShowHiddenChange: (value: boolean) => void;
  onClearAll: () => void;
}

const RISK_LEVEL_LABELS: Record<RiskLevel, string> = {
  HIGH: "High",
  MEDIUM: "Medium",
  LOW: "Low",
  UNKNOWN: "Unknown",
};

const BADGE_CLASSES =
  "h-6 px-1 pr-1 bg-[#E2E2E2] text-[#666666] text-xs font-medium rounded-full flex items-center gap-1 hover:bg-[#d0d0d0]";
const REMOVE_BUTTON_CLASSES =
  "h-6 w-6 p-0 rounded-full hover:bg-[#cccccc] text-[#666666]";

export function ActiveResidentsFilters({
  selectedRiskLevels,
  onRiskLevelsChange,
  daysSinceReview,
  onDaysSinceReviewChange,
  selectedBuilding,
  onBuildingChange,
  buildingOptions,
  selectedStation,
  onStationChange,
  stationOptions,
  showHidden,
  onShowHiddenChange,
  onClearAll,
}: ActiveResidentsFiltersProps) {
  const hasActiveFilters =
    selectedRiskLevels.length > 0 ||
    daysSinceReview.trim() !== "" ||
    (selectedBuilding && selectedBuilding !== "all") ||
    (selectedStation && selectedStation.length > 0) ||
    showHidden;

  if (!hasActiveFilters) {
    return null;
  }

  const removeRiskLevel = (level: RiskLevel) => {
    onRiskLevelsChange(selectedRiskLevels.filter((l) => l !== level));
  };

  const getBuildingLabel = (value: string): string => {
    const option = buildingOptions.find((o) => o.value === value);
    return option?.label ?? value;
  };

  const getStationLabel = (value: string): string => {
    const option = stationOptions.find((o) => o.value === value);
    return option?.label ?? value;
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex items-center gap-2 flex-wrap">
          {/* Risk Level Badges */}
          {selectedRiskLevels.map((level) => (
            <Badge key={level} variant="secondary" className={BADGE_CLASSES}>
              <span className="px-1">
                Risk: {RISK_LEVEL_LABELS[level]}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeRiskLevel(level)}
                className={REMOVE_BUTTON_CLASSES}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Days Since Review Badge */}
          {daysSinceReview.trim() && (
            <Badge variant="secondary" className={BADGE_CLASSES}>
              <span className="px-1">
                Days Since Review: {daysSinceReview}+
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDaysSinceReviewChange("")}
                className={REMOVE_BUTTON_CLASSES}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Building Badge */}
          {selectedBuilding && selectedBuilding !== "all" && (
            <Badge variant="secondary" className={BADGE_CLASSES}>
              <span className="px-1">
                Building: {getBuildingLabel(selectedBuilding)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onBuildingChange("all")}
                className={REMOVE_BUTTON_CLASSES}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}

          {/* Station Badges */}
          {selectedStation && selectedStation.map((station) => (
            <Badge key={station} variant="secondary" className={BADGE_CLASSES}>
              <span className="px-1">
                Station: {getStationLabel(station)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onStationChange(selectedStation.filter((s) => s !== station))}
                className={REMOVE_BUTTON_CLASSES}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))}

          {/* Show Hidden Badge */}
          {showHidden && (
            <Badge variant="secondary" className={BADGE_CLASSES}>
              <span className="px-1">Showing Hidden</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShowHiddenChange(false)}
                className={REMOVE_BUTTON_CLASSES}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          )}
      </div>

      {/* Clear All Button */}
      <Button
        variant="ghost"
        onClick={onClearAll}
        className="h-6 px-1 text-[#228189] hover:bg-gray-50 font-bold text-sm shrink-0"
      >
        Clear all
      </Button>
    </div>
  );
}
